/*
 * @Author: Yun 
 * @Date: 2025-11-24 00:00:00 
 * @Last Modified by: Yun
 * @Last Modified time: 2025-11-28 17:25:57
 */

import React, { useRef } from 'react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { content } from '@configs/content';
import styles from './index.module.scss';

gsap.registerPlugin(useGSAP, ScrollTrigger);

// 生成随机尺寸变体（更大的尺寸差异）
const getSizeVariant = (index: number): 'small' | 'medium' | 'large' => {
  const variants: ('small' | 'medium' | 'large')[] = ['large', 'small',  'medium', 'large', 'small', 'medium', 'large', 'medium', 'medium'];
  return variants[index % variants.length];
};

// 统一的 margin 配置数组，按顺序对应每个元素
// 左边元素使用 marginLeft，右边元素使用 marginRight
const getMargin = (index: number): number => {
  const margins = [5, 10, 35, 5, 12, 30, 5, 5, 17];
  return margins[index % margins.length];
}

// 生成随机间距 - 使用 vw 单位保持与豆腐块大小成比例
const getRandomSpacing = (index: number): number => {
  const spacings = [-15, -1, 10, -15, -3, 10, -15, -7, 0];
  return spacings[index % spacings.length];
};

const ProductCollection: React.FC = () => {
  const { items, background } = content.productCollection;
  const containerRef = useRef<HTMLDivElement>(null);
  const itemsContainerRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    if (!containerRef.current || !itemsContainerRef.current) return;

    // 计算总的内容高度（所有节点 + 间距）
    const totalContentHeight = items.length * 15; // 平均每个节点约15vh（包含间距）
    const scrollDistance = totalContentHeight * 5; // 滚动距离缩短

    // 创建主时间轴，固定整个 section
    const mainTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: containerRef.current,
        start: 'top top',
        end: `+=${scrollDistance}%`,
        pin: true,
        scrub: 1,
        anticipatePin: 1,
        invalidateOnRefresh: true,
      }
    });

    mainTimeline.fromTo(
      itemsContainerRef.current,
      { y: 0 },
      {
        y: () => {
          // 动态计算需要滚动的距离
          const containerHeight = itemsContainerRef.current?.scrollHeight || 0;
          const viewportHeight = window.innerHeight;
          return -(containerHeight - viewportHeight * 1.4);
        },
        ease: 'none',
        duration: 1,
      }
    );

  }, { scope: containerRef, dependencies: [items] });

  return (
    <section ref={containerRef} className={styles.productSection} id="product">
      {/* 背景图层 - 固定背景 */}
      <div 
        className={styles.background}
        style={{ backgroundImage: `url(${background})` }}
      />
      
      {/* 节点容器 - 整体滚动 */}
      <div ref={itemsContainerRef} className={styles.itemsContainer}>
        {items.map((item, index) => {
          const isLeft = index % 2 === 0;
          const sizeVariant = getSizeVariant(index);
          const spacing = getRandomSpacing(index);
          const margin = getMargin(index);
          
          return (
            <div
              key={item.year}
              data-year={item.year}
              className={`${styles.item} ${isLeft ? styles.itemLeft : styles.itemRight}`}
              style={{ 
                marginBottom: `${spacing}vw`, 
                marginLeft: isLeft ? `${margin}%` : 'auto', 
                marginRight: !isLeft ? `${margin}%` : 'auto' 
              }}
            >
              <div className={styles.yearLabel}>{item.year}</div>
              <a 
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`${styles.glassCard} ${styles[`size-${sizeVariant}`]}`}
              >
                <div className={styles.cardHeader}>
                  <p className={styles.itemTitle}>{item.title}</p>
                </div>
                <div className={styles.cardImage}>
                  <img src={item.image} alt={`${item.year} - ${item.title}`} />
                </div>
              </a>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default ProductCollection;
